import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  loginWithSSO,
  selectCompany,
  refreshBolsterupToken,
} from "./auth-service";
import type { BolsterupUser, Company } from "@/types";

interface AuthState {
  status: "idle" | "loading" | "authenticated" | "select-company" | "error";
  user: BolsterupUser | null;
  companies: Company[];
  selectedCompany: Company | null;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  chooseCompany: (company: Company) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = "bolsterup_addin_auth";

function loadPersistedAuth(): Partial<AuthState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Check token expiry
      if (parsed.user?.newAccessTokenExpiry > Date.now()) {
        return {
          status: "authenticated",
          user: parsed.user,
          selectedCompany: parsed.selectedCompany,
          companies: parsed.companies || [],
        };
      }
    }
  } catch {
    // Corrupted storage, ignore
  }
  return {};
}

function persistAuth(state: Partial<AuthState>) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        user: state.user,
        selectedCompany: state.selectedCompany,
        companies: state.companies,
      })
    );
  } catch {
    // Storage full or unavailable
  }
}

function clearPersistedAuth() {
  localStorage.removeItem(STORAGE_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const persisted = loadPersistedAuth();

  const [state, setState] = useState<AuthState>({
    status: persisted.status || "idle",
    user: persisted.user || null,
    companies: persisted.companies || [],
    selectedCompany: persisted.selectedCompany || null,
    error: null,
  });

  // Token refresh loop
  useEffect(() => {
    if (state.status !== "authenticated" || !state.user) return;

    const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry

    const checkAndRefresh = async () => {
      if (!state.user) return;
      const timeToExpiry = state.user.newAccessTokenExpiry - Date.now();

      if (timeToExpiry <= REFRESH_THRESHOLD_MS) {
        try {
          const tokens = await refreshBolsterupToken(state.user.refreshToken);
          if (tokens.expired) {
            setState((s) => ({ ...s, status: "idle", user: null }));
            clearPersistedAuth();
            return;
          }

          setState((s) => {
            const updated = {
              ...s,
              user: s.user
                ? {
                    ...s.user,
                    accessToken: tokens.accessToken,
                    sessionToken: tokens.sessionToken,
                    refreshToken: tokens.refreshToken,
                    newAccessTokenExpiry: tokens.newAccessTokenExpiry,
                  }
                : null,
            };
            persistAuth(updated);
            return updated;
          });
        } catch {
          // Refresh failed, user will need to re-authenticate
          setState((s) => ({ ...s, status: "idle", user: null }));
          clearPersistedAuth();
        }
      }
    };

    const intervalId = window.setInterval(checkAndRefresh, 60 * 1000);
    checkAndRefresh(); // Check immediately

    return () => clearInterval(intervalId);
  }, [state.status, state.user?.newAccessTokenExpiry]);

  const login = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading", error: null }));

    try {
      const result = await loginWithSSO();

      if (result.error) {
        setState((s) => ({
          ...s,
          status: "error",
          error:
            result.message || "Login failed. Please check your Bolsterup account.",
        }));
        return;
      }

      const companies = result.companies || [];
      const user = result.user!;

      if (companies.length === 0) {
        // User has no companies — go straight to authenticated
        setState((s) => ({
          ...s,
          status: "authenticated",
          user,
          companies: [],
          selectedCompany: null,
        }));
        persistAuth({ user, companies: [], selectedCompany: null });
      } else if (companies.length === 1) {
        // Single company — auto-select
        const company = companies[0];
        const updated = await selectCompany(user.accessToken, company.id);
        const mergedUser = { ...user, ...updated, activeCompanyId: company.id };
        setState((s) => ({
          ...s,
          status: "authenticated",
          user: mergedUser,
          companies,
          selectedCompany: company,
        }));
        persistAuth({
          user: mergedUser,
          companies,
          selectedCompany: company,
        });
      } else {
        // Multiple companies — show picker
        setState((s) => ({
          ...s,
          status: "select-company",
          user,
          companies,
        }));
      }
    } catch (err: any) {
      setState((s) => ({
        ...s,
        status: "error",
        error: err.message || "Authentication failed",
      }));
    }
  }, []);

  const chooseCompany = useCallback(
    async (company: Company) => {
      if (!state.user) return;

      setState((s) => ({ ...s, status: "loading" }));

      try {
        const updated = await selectCompany(
          state.user.accessToken,
          company.id
        );
        const mergedUser = {
          ...state.user,
          ...updated,
          activeCompanyId: company.id,
        };
        setState((s) => ({
          ...s,
          status: "authenticated",
          user: mergedUser,
          selectedCompany: company,
        }));
        persistAuth({
          user: mergedUser,
          companies: state.companies,
          selectedCompany: company,
        });
      } catch (err: any) {
        setState((s) => ({
          ...s,
          status: "error",
          error: err.message || "Failed to select organization",
        }));
      }
    },
    [state.user, state.companies]
  );

  const logout = useCallback(() => {
    clearPersistedAuth();
    setState({
      status: "idle",
      user: null,
      companies: [],
      selectedCompany: null,
      error: null,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, login, chooseCompany, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
